import { useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Copy,
  FileText,
  Link2,
  Loader2,
  Search,
  Trash2
} from 'lucide-react';
import { useCatalogStore } from '../store/catalogStore';
import { copyText } from '../lib/exportCatalog';
import { generateCatalogFromPdf } from '../lib/pdfGenerator';
import { publishCatalog } from '../lib/publishCatalog';
import { hasPublicSiteUrl, localFilePath, publicFileUrl } from '../lib/siteUrl';
import type { CatalogMetadata, GenerationProgress } from '../types';

export function AdminDashboard() {
  const catalogs = useCatalogStore((state) => state.catalogs);
  const upsertCatalog = useCatalogStore((state) => state.upsertCatalog);
  const deleteCatalog = useCatalogStore((state) => state.deleteCatalog);
  const updateCatalog = useCatalogStore((state) => state.updateCatalog);
  const [query, setQuery] = useState('');
  const [title, setTitle] = useState('');
  const [progress, setProgress] = useState<GenerationProgress | null>(null);
  const [selected, setSelected] = useState<CatalogMetadata | null>(catalogs[0] ?? null);
  const [isBusy, setIsBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const shareableUrl = (catalog: CatalogMetadata) => publicFileUrl(catalog.slug);
  const displayUrl = selected ? shareableUrl(selected) : '';
  const hasPublishAssets = (catalog: CatalogMetadata) =>
    catalog.pages.some((page) => page.fullUrl.startsWith('data:')) || Boolean(catalog.sourcePdfDataUrl);
  const statusLabel = (status: CatalogMetadata['status']) => {
    if (status === 'published') return 'pronto';
    if (status === 'publishing' || status === 'generating') return 'creazione';
    if (status === 'error') return 'errore';
    return 'bozza';
  };

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return catalogs;
    return catalogs.filter((catalog) =>
      [catalog.title, catalog.slug, catalog.publicUrl].some((value) => value.toLowerCase().includes(needle))
    );
  }, [catalogs, query]);

  useEffect(() => {
    if (isBusy) return;
    if (selected && catalogs.some((catalog) => catalog.slug === selected.slug)) return;
    setSelected(catalogs[0] ?? null);
  }, [catalogs, isBusy, selected]);

  const onDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setIsBusy(true);
    setErrorMessage('');
    try {
      const catalog = await generateCatalogFromPdf(file, title || file.name.replace(/\.pdf$/i, ''), setProgress);
      setProgress({ phase: 'Caricamento online', current: 0, total: 1 });
      setSelected({ ...catalog, status: 'publishing' });
      const published = await publishCatalog({ ...catalog, status: 'publishing' });
      setProgress({ phase: 'Link pronto', current: 1, total: 1 });
      const next = { ...published, publicUrl: publicFileUrl(published.slug) || localFilePath(published.slug) };
      upsertCatalog(next);
      setSelected(next);
      setTitle('');
      window.location.href = localFilePath(next.slug);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Errore durante la pubblicazione.');
    } finally {
      setIsBusy(false);
      setProgress(null);
    }
  };

  const dropzone = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    disabled: isBusy
  });

  const publish = async (catalog: CatalogMetadata) => {
    setIsBusy(true);
    setErrorMessage('');
    try {
      if (!hasPublishAssets(catalog) && catalog.status !== 'published') {
        throw new Error('Ricarica il PDF: i file sorgente non sono piu disponibili in questa sessione.');
      }
      const publishing = { ...catalog, status: 'publishing' as const };
      setSelected(publishing);
      const published = await publishCatalog(publishing);
      const next = { ...published, publicUrl: publicFileUrl(published.slug) || localFilePath(published.slug) };
      upsertCatalog(next);
      setSelected(next);
      const url = shareableUrl(next);
      if (url) await copyText(url);
      return next;
    } catch (error) {
      const next = { ...catalog, status: 'error' as const };
      upsertCatalog(next);
      setSelected(next);
      setErrorMessage(error instanceof Error ? error.message : 'Errore durante la pubblicazione.');
      return null;
    } finally {
      setIsBusy(false);
    }
  };

  const openCatalog = async (catalog: CatalogMetadata) => {
    setSelected(catalog);
    const published = catalog.status === 'published' ? catalog : await publish(catalog);
    if (published) {
      window.open(localFilePath(published.slug), '_blank', 'noopener,noreferrer');
    }
  };

  const renameSelected = (value: string) => {
    if (!selected) return;
    updateCatalog(selected.slug, { title: value });
    setSelected({ ...selected, title: value, updated: new Date().toISOString() });
  };

  return (
    <main className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brandMark">PDF</div>
          <div>
            <strong>File PDF</strong>
            <span>Link privati</span>
          </div>
        </div>

        <nav className="navList">
          <a className="navItem active" href="/admin">
            <FileText size={17} />
            File PDF
          </a>
        </nav>
      </aside>

      <section className="workspace">
        <header className="topbar">
          <div>
            <h1>PDF sfogliabili</h1>
            <p>Carica un PDF e si apre subito come libro sfogliabile.</p>
          </div>
          <div className="searchBox">
            <Search size={16} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cerca file PDF" />
          </div>
        </header>

        <section className="uploadBand">
          <div>
            <label className="fieldLabel" htmlFor="title">
              Nome file
            </label>
            <input
              id="title"
              className="titleInput"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Nome PDF"
              autoComplete="off"
            />
          </div>
          <div {...dropzone.getRootProps({ className: `dropzone ${dropzone.isDragActive ? 'isActive' : ''}` })}>
            <input {...dropzone.getInputProps()} />
            {isBusy ? <Loader2 className="spin" size={22} /> : <FileText size={22} />}
            <span>{isBusy ? progress?.phase ?? 'Generazione' : 'Trascina un PDF o clicca: poi si apre da solo'}</span>
            {progress && <progress value={progress.current} max={progress.total} />}
          </div>
        </section>

        {errorMessage && <div className="errorBanner">{errorMessage}</div>}

        <section className="contentGrid">
          <div className="tablePanel">
            <div className="panelHeader">
              <h2>File PDF</h2>
              <span>{filtered.length}</span>
            </div>
            <div className="catalogTable">
              {filtered.map((catalog) => (
                <button
                  className={`catalogRow ${selected?.slug === catalog.slug ? 'selected' : ''}`}
                  key={catalog.slug}
                  onClick={() => openCatalog(catalog)}
                >
                  <span className="rowTitle">{catalog.title}</span>
                  <span>{catalog.pageCount} pagine</span>
                  <span>{new Date(catalog.updated).toLocaleDateString('it-IT')}</span>
                  <span className={`status ${catalog.status}`}>{statusLabel(catalog.status)}</span>
                </button>
              ))}
              {filtered.length === 0 && <div className="emptyState">Nessun file PDF ancora. Carica un PDF per iniziare.</div>}
            </div>
          </div>

          <aside className="detailPanel">
            {selected ? (
              <>
                <div className="panelHeader">
                  <h2>Dettaglio</h2>
                  <button
                    className="iconButton danger"
                    title="Elimina"
                    onClick={() => {
                      deleteCatalog(selected.slug);
                      setSelected(null);
                    }}
                  >
                    <Trash2 size={17} />
                  </button>
                </div>
                <label className="fieldLabel" htmlFor="selected-title">
                  Titolo
                </label>
                <input
                  id="selected-title"
                  className="titleInput compact"
                  value={selected.title}
                  onChange={(event) => renameSelected(event.target.value)}
                />
                <dl className="metaList">
                  <div>
                    <dt>Slug</dt>
                    <dd>{selected.slug}</dd>
                  </div>
                  <div>
                    <dt>Pagine</dt>
                    <dd>{selected.pageCount}</dd>
                  </div>
                  <div>
                    <dt>Link file</dt>
                    <dd>{displayUrl || 'Link WhatsApp/mail non ancora configurato'}</dd>
                  </div>
                </dl>
                {!hasPublicSiteUrl() && (
                  <div className="shareWarning">
                    Nessun link locale viene mostrato. Per copiare un link WhatsApp/mail serve prima il dominio Cloudflare Pages.
                  </div>
                )}
                <div className="actionGrid">
                  <button onClick={() => displayUrl && copyText(displayUrl)} disabled={!displayUrl}>
                    <Copy size={16} />
                    Copia link
                  </button>
                  <button onClick={() => openCatalog(selected)} disabled={isBusy}>
                    <Link2 size={16} />
                    Apri file
                  </button>
                </div>
              </>
            ) : (
              <div className="emptyState">Seleziona un file PDF.</div>
            )}
          </aside>
        </section>
      </section>
    </main>
  );
}
